import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class PreloadService {
  private preloadedResources = new Set<string>();

  constructor() {}

  // Precargar imágenes críticas
  preloadCriticalImages(imageUrls: string[]): Promise<void[]> {
    const promises = imageUrls.map(url => {
      if (this.preloadedResources.has(url)) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          this.preloadedResources.add(url);
          resolve();
        };
        img.onerror = () => {
          console.warn(`Failed to preload image: ${url}`);
          reject(new Error(`Failed to preload image: ${url}`));
        };
        img.src = url;
      });
    });

    return Promise.all(promises);
  }

  // Precargar videos críticos
  preloadCriticalVideos(videoUrls: string[]): Promise<void[]> {
    const promises = videoUrls.map(url => {
      if (this.preloadedResources.has(url)) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          this.preloadedResources.add(url);
          resolve();
        };
        video.onerror = () => {
          console.warn(`Failed to preload video: ${url}`);
          reject(new Error(`Failed to preload video: ${url}`));
        };
        video.src = url;
      });
    });

    return Promise.all(promises);
  }

  // Precargar fuentes críticas
  preloadCriticalFonts(fontUrls: string[]): Promise<void[]> {
    const promises = fontUrls.map(url => {
      if (this.preloadedResources.has(url)) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'font';
        link.type = 'font/woff2';
        link.crossOrigin = 'anonymous';
        link.href = url;
        
        link.onload = () => {
          this.preloadedResources.add(url);
          resolve();
        };
        link.onerror = () => {
          console.warn(`Failed to preload font: ${url}`);
          reject(new Error(`Failed to preload font: ${url}`));
        };
        
        document.head.appendChild(link);
      });
    });

    return Promise.all(promises);
  }

  // Precargar recursos críticos de la aplicación
  async preloadCriticalResources(): Promise<void> {
    try {
      // Precargar imágenes críticas del landing
      const criticalImages = [
        'assets/img/video-backgrounds/background-video-introduction.jpg',
        'assets/icons/play-video.svg',
        'assets/icons/cuaderno-persona-white.svg',
        'assets/icons/flecha-white.svg',
        'assets/icons/rayado-lapiz-white.svg',
        'assets/icons/monitor-figuras-white.svg',
        'assets/icons/check-paper-white.svg',
        'assets/icons/monitor-ojo-white.svg',
        'assets/icons/persona-white.svg'
      ];

      // Precargar videos críticos
      const criticalVideos = [
        'assets/videos/video-introduction.MOV'
      ];

      // Precargar fuentes críticas
      const criticalFonts = [
        'assets/fonts/Avenir Book.ttf',
        'assets/fonts/Avenir Heavy.ttf',
        'assets/fonts/Avenir Black.ttf'
      ];

      // Ejecutar precarga en paralelo
      await Promise.all([
        this.preloadCriticalImages(criticalImages),
        this.preloadCriticalVideos(criticalVideos),
        this.preloadCriticalFonts(criticalFonts)
      ]);

      console.log('✅ Critical resources preloaded successfully');
    } catch (error) {
      console.warn('⚠️ Some critical resources failed to preload:', error);
    }
  }

  // Verificar si un recurso ya fue precargado
  isResourcePreloaded(url: string): boolean {
    return this.preloadedResources.has(url);
  }

  // Obtener lista de recursos precargados
  getPreloadedResources(): string[] {
    return Array.from(this.preloadedResources);
  }
}
